/* prettier-ignore-file */
/**
 * Product availability for a date range.
 *
 * All availability in this app is computed client-side by scanning the bookings
 * held in the `useBookings` Zustand store -- there is no server-side inventory
 * logic. This module follows the per-day approach from
 * EditBooking.js:1324-1488, which is the correct one of the three
 * implementations in the codebase:
 *
 *   - An item is only bookable if it is free for EVERY day in the range, so we
 *     take the minimum availability across the range rather than testing the
 *     range as a single overlap window.
 *   - Items are matched by catalog `id`. NewBooking.js:1043 matches product1 by
 *     `name` instead, and passes `booking.product2` (an array) to
 *     Object.entries() as though it were a keyed object -- so product2
 *     quantities are silently never counted there. We match by id, with a name
 *     fallback for legacy documents whose id was written as the product name
 *     (see the fallback at NewBooking.js:2114).
 *
 * Inventory fields differ by product type: product1 uses `quantity`, product2
 * uses `inventory`. A null/absent value means untracked, i.e. unlimited.
 */

import { parseRentalDate } from "./rentalPricing";

/**
 * How long an unconfirmed booking holds inventory.
 *
 * A hold is a DERIVED FACT, not a lock: "a pending booking younger than this". There is
 * deliberately no release step, because any release step can fail — if a customer's
 * device crashes mid-checkout there is nothing left to run, and a lock-with-release
 * design would strand that inventory forever. Expiry here is a property of the query,
 * so a stranded hold is unrepresentable.
 *
 * 10 minutes frees an abandoned booking quickly on a busy day. It is deliberately
 * tighter than an unhurried checkout: a customer who lingers past it can lose the hold
 * mid-payment, which the oversell backstop in the confirmation webhook then flags
 * (findOversoldItems in functions/index.js). Cash refuses outright in that case.
 */
export const HOLD_WINDOW_MS = 10 * 60 * 1000;

/** Pending bookings still young enough to hold inventory. */
export const activeHolds = (pendingBookings, nowMillis = Date.now()) =>
  (pendingBookings || []).filter((pending) => {
    if (!pending || pending.cancelled === true) return false;
    // createdAt is a Firestore Timestamp on these documents.
    const created = parseRentalDate(pending.createdAt);
    if (!created) return false; // no age -> cannot prove it is fresh -> do not hold
    return nowMillis - created.getTime() < HOLD_WINDOW_MS;
  });

/** Local-midnight copy of a date. */
const atMidnight = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

/** Every local-midnight Date from start through end, inclusive. */
export const eachDayInRange = (startDate, endDate) => {
  const start = parseRentalDate(startDate);
  const end = parseRentalDate(endDate);
  if (!start || !end) return [];
  const days = [];
  const cursor = atMidnight(start);
  const last = atMidnight(end);
  while (cursor <= last) {
    days.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
};

/**
 * Bookings that actually hold inventory.
 *
 * Cancelled bookings are excluded. The bookings cache subscribes without a
 * status filter (bookingsCache.js:27), so the store does contain cancelled
 * documents; EditBooking's availability pass does not filter them, which
 * under-reports availability. Bookings.js:329 does filter them, and that is the
 * correct reading -- a cancelled booking is not holding a bike.
 */
const relevantBookings = (bookings, excludeBookingId) =>
  (bookings || []).filter((booking) => {
    if (!booking) return false;
    if (booking.cancelled === true) return false;
    if (!booking.startDate || !booking.endDate) return false;
    if (excludeBookingId) {
      if (booking.id === excludeBookingId || booking.bookingId === excludeBookingId) return false;
    }
    return true;
  });

/** Does a booking cover this specific day? */
const bookingCoversDay = (booking, dayMillis) => {
  const start = parseRentalDate(booking.startDate);
  const end = parseRentalDate(booking.endDate);
  if (!start || !end) return false;
  return atMidnight(start).getTime() <= dayMillis && atMidnight(end).getTime() >= dayMillis;
};

/**
 * Units of one catalog product committed on a given day.
 * `field` is "product1" or "product2" on the booking document.
 */
const bookedUnitsOnDay = (bookingsToCheck, dayMillis, field, catalogProduct) => {
  let booked = 0;
  bookingsToCheck.forEach((booking) => {
    if (!bookingCoversDay(booking, dayMillis)) return;
    const items = booking[field];
    if (!Array.isArray(items)) return;
    items.forEach((item) => {
      if (!item) return;
      const matches =
        item.id === catalogProduct.id ||
        // Legacy documents may carry the product name where an id belongs.
        (item.id && catalogProduct.name && item.id === catalogProduct.name) ||
        (item.name && catalogProduct.name && item.name === catalogProduct.name);
      if (matches) booked += item.quantity || 0;
    });
  });
  return booked;
};

/**
 * Availability for one catalog list over a date range.
 * Returns { [productId]: { available, total, booked, hasInventory } }.
 * `available` is null when the product has no inventory tracking (unlimited).
 */
const availabilityForCatalog = ({ catalog, confirmed, holds, days, field, inventoryField }) => {
  const result = {};
  (catalog || []).forEach((catalogProduct) => {
    const total = Number(catalogProduct?.[inventoryField]);
    const hasInventory = Number.isFinite(total) && total > 0;

    if (!hasInventory) {
      result[catalogProduct.id] = {
        available: null,
        total: null,
        booked: 0,
        held: 0,
        hasInventory: false,
      };
      return;
    }

    // An item must be free for the entire range, so take the worst day.
    let minAvailable = Infinity;
    let maxBooked = 0;
    let maxHeld = 0;
    days.forEach((day) => {
      const millis = day.getTime();
      const booked = bookedUnitsOnDay(confirmed, millis, field, catalogProduct);
      // Tracked separately so staff can distinguish "sold" from "someone is
      // mid-checkout" — the second will free itself, the first will not.
      const held = bookedUnitsOnDay(holds, millis, field, catalogProduct);
      const available = Math.max(0, total - booked - held);
      if (available < minAvailable) minAvailable = available;
      if (booked > maxBooked) maxBooked = booked;
      if (held > maxHeld) maxHeld = held;
    });

    result[catalogProduct.id] = {
      available: minAvailable === Infinity ? total : minAvailable,
      total,
      booked: maxBooked,
      held: maxHeld,
      hasInventory: true,
    };
  });
  return result;
};

/**
 * Availability of every catalog product across the selected range.
 *
 * @returns {{ product1: Object, product2: Object, days: number }}
 *          Each map is keyed by catalog product id.
 */
export const computeAvailabilityForRange = ({
  startDate,
  endDate,
  bookings = [],
  // Unconfirmed checkouts. Counted while young so two customers cannot both be sold
  // the last item; stale ones simply stop counting. See HOLD_WINDOW_MS.
  pendingBookings = [],
  products1Catalog = [],
  products2Catalog = [],
  excludeBookingId = null,
}) => {
  const days = eachDayInRange(startDate, endDate);
  if (days.length === 0) {
    return { product1: {}, product2: {}, days: 0 };
  }

  const confirmed = relevantBookings(bookings, excludeBookingId);
  const holds = relevantBookings(activeHolds(pendingBookings), excludeBookingId);

  return {
    product1: availabilityForCatalog({
      catalog: products1Catalog,
      confirmed,
      holds,
      days,
      field: "product1",
      inventoryField: "quantity",
    }),
    product2: availabilityForCatalog({
      catalog: products2Catalog,
      confirmed,
      holds,
      days,
      field: "product2",
      inventoryField: "inventory",
    }),
    days: days.length,
  };
};

/**
 * Product1 availability for every day of a month, for calendar shading.
 * Returns { "YYYY-MM-DD": { totalAvailable, byProduct: { [id]: available } } }.
 */
export const computeMonthAvailability = ({
  month,
  bookings = [],
  pendingBookings = [],
  products1Catalog = [],
  excludeBookingId = null,
}) => {
  const result = {};
  if (!month) return result;

  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const bookingsToCheck = [
    ...relevantBookings(bookings, excludeBookingId),
    ...relevantBookings(activeHolds(pendingBookings), excludeBookingId),
  ];

  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = new Date(year, monthIndex, day);
    date.setHours(0, 0, 0, 0);
    const dayMillis = date.getTime();
    // Local-date key -- toISOString() would shift the day for users west of UTC.
    const key = `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

    const byProduct = {};
    let totalAvailable = 0;

    (products1Catalog || []).forEach((catalogProduct) => {
      const total = Number(catalogProduct?.quantity);
      if (!Number.isFinite(total) || total <= 0) {
        byProduct[catalogProduct.id] = null;
        return;
      }
      const booked = bookedUnitsOnDay(bookingsToCheck, dayMillis, "product1", catalogProduct);
      const available = Math.max(0, total - booked);
      byProduct[catalogProduct.id] = available;
      totalAvailable += available;
    });

    result[key] = { totalAvailable, byProduct };
  }

  return result;
};

/** Local-date key matching computeMonthAvailability's keys. */
export const toDayKey = (date) => {
  const d = parseRentalDate(date);
  if (!d) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};
